// Iter 387 — Vista previa + ejecución de la aplicación manual (pares anticipo → documento) y resultado.
import { useMemo, useState } from 'react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { CheckCircle2, XCircle, Loader2, Sparkles, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { fmtFecha } from '../../../lib/utils';
import { fmtCLP, LADO_INFO } from './regularizacionUtils';

const DIAS_MAX = 20;

export function AplicarPreviewDialog({ lado, rut, pares: iniciales, anticipos, documentos, onClose, onDone }) {
  const info = LADO_INFO[lado];
  const [pares, setPares] = useState(iniciales);
  const [saving, setSaving] = useState(false);
  const [resultado, setResultado] = useState(null);
  const antPorId = useMemo(() => Object.fromEntries(anticipos.map((a) => [a.id, a])), [anticipos]);
  const docPorId = useMemo(() => Object.fromEntries(documentos.map((d) => [d.id, d])), [documentos]);

  const { errores, advertencias, sumAnts, sumDocs } = useMemo(() => {
    const porA = {}; const porD = {}; const errs = []; const warns = [];
    pares.forEach((p) => {
      const m = Math.round(Number(p.monto) || 0);
      if (m <= 0) errs.push(`Monto inválido en ${antPorId[p.anticipo_id]?.ref || '?'} → ${docPorId[p.documento_id]?.numero || '?'}`);
      porA[p.anticipo_id] = (porA[p.anticipo_id] || 0) + m;
      porD[p.documento_id] = (porD[p.documento_id] || 0) + m;
      const a = antPorId[p.anticipo_id]; const d = docPorId[p.documento_id];
      if (a?.fecha && d?.fecha) {
        const dias = Math.abs(Math.round((new Date(a.fecha.slice(0, 10)) - new Date(d.fecha.slice(0, 10))) / 86400000));
        if (dias > DIAS_MAX) warns.push(`${info.anticipo} N°${a.ref} (${fmtFecha(a.fecha)}) y ${info.documento} ${d.numero} (${fmtFecha(d.fecha)}) distan ${dias} días (> ${DIAS_MAX}): revisa que correspondan a lo mismo`);
      }
    });
    const totA = Object.keys(porA).reduce((s, id) => s + Math.round(antPorId[id]?.disponible || 0), 0);
    const totD = Object.keys(porD).reduce((s, id) => s + Math.round(docPorId[id]?.saldo || 0), 0);
    if (pares.length && totA !== totD) errs.push(`La selección no cuadra: anticipos ${fmtCLP(totA)} vs documentos ${fmtCLP(totD)} (diferencia ${fmtCLP(totA - totD)}). Σ anticipos debe ser igual a Σ documentos`);
    Object.entries(porA).forEach(([id, m]) => { const disp = Math.round(antPorId[id]?.disponible || 0); if (m !== disp) errs.push(`${info.anticipo} N°${antPorId[id]?.ref}: aplica ${fmtCLP(m)} pero su disponible es ${fmtCLP(disp)} (debe consumirse completo)`); });
    Object.entries(porD).forEach(([id, m]) => { const saldo = Math.round(docPorId[id]?.saldo || 0); if (m !== saldo) errs.push(`${info.documento} ${docPorId[id]?.numero}: aplica ${fmtCLP(m)} pero su saldo es ${fmtCLP(saldo)} (debe quedar pagado completo)`); });
    return { errores: errs, advertencias: warns, sumAnts: totA, sumDocs: totD };
  }, [pares, antPorId, docPorId, info]);

  const total = pares.reduce((s, p) => s + (Number(p.monto) || 0), 0);

  const aplicar = async () => {
    setSaving(true);
    try {
      const r = await api.post('/contabilidad/regularizacion/aplicar', { lado, rut, pares: pares.map((p) => ({ anticipo_id: p.anticipo_id, documento_id: String(p.documento_id), monto: Number(p.monto) })) });
      setResultado(r.data);
      if (r.data.ok) toast.success(`${r.data.aplicados} aplicación(es) por ${fmtCLP(r.data.monto_aplicado)} registradas en Alegra`);
      else toast.warning(`${r.data.aplicados} aplicadas · ${r.data.fallidos} rechazadas por Alegra`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo aplicar');
    } finally {
      setSaving(false);
    }
  };

  const cerrar = () => { if (resultado) onDone(); onClose(); };

  return (
    <Dialog open onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-4xl" data-testid="dialog-aplicar-preview">
        <DialogHeader>
          <DialogTitle className="text-base">{resultado ? 'Resultado de la aplicación' : 'Confirmar aplicación manual'}</DialogTitle>
          <DialogDescription className="text-xs">
            {resultado
              ? 'Alegra quedó actualizado: el documento figura pagado total o parcialmente y el anticipo consumido en la misma proporción.'
              : `Cada par hace un PUT en Alegra sobre el ${info.anticipo} agregando la ${info.documento.toLowerCase()}. Solo se aplica si cuadra exacto: cada ${info.documento.toLowerCase()} queda ${info.accionDoc === 'pago' ? 'pagada' : 'cobrada'} completa y cada ${info.anticipo.toLowerCase()} consumido completo.`}
          </DialogDescription>
        </DialogHeader>

        {!resultado && (
          <div className="border rounded max-h-[50vh] overflow-y-auto">
            <table className="w-full text-xs" data-testid="tabla-pares">
              <thead className="bg-muted sticky top-0">
                <tr><th className="text-left px-2 py-1.5">{info.anticipo}</th><th className="text-left px-2 py-1.5">{info.documento}</th>
                  <th className="text-left px-2 py-1.5">Motivo</th><th className="text-right px-2 py-1.5 w-40">Monto a aplicar</th><th className="w-8" /></tr>
              </thead>
              <tbody>
                {pares.map((p, i) => {
                  const a = antPorId[p.anticipo_id]; const d = docPorId[p.documento_id];
                  return (
                    <tr key={`${p.anticipo_id}-${p.documento_id}`} className="border-t" data-testid={`par-${i}`}>
                      <td className="px-2 py-1"><div className="font-mono">N°{a?.ref || '—'}</div><div className="text-[10px] text-muted-foreground">disp. {fmtCLP(a?.disponible)}</div></td>
                      <td className="px-2 py-1"><div className="font-mono">{d?.numero}</div><div className="text-[10px] text-muted-foreground">saldo {fmtCLP(d?.saldo)}</div></td>
                      <td className="px-2 py-1 text-muted-foreground">{p.fuerte && <Sparkles className="w-3 h-3 inline text-indigo-600 mr-1" />}{p.motivo || 'selección manual'}</td>
                      <td className="px-2 py-1 text-right">
                        <Input type="number" min={1} value={p.monto} className="h-7 text-xs text-right" data-testid={`par-monto-${i}`}
                          onChange={(e) => setPares((prev) => prev.map((x, j) => (j === i ? { ...x, monto: e.target.value } : x)))} />
                      </td>
                      <td className="px-1 py-1"><button type="button" className="text-muted-foreground hover:text-red-600" onClick={() => setPares((prev) => prev.filter((_, j) => j !== i))} data-testid={`par-quitar-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  );
                })}
                <tr className="border-t bg-slate-50 font-semibold"><td className="px-2 py-1.5" colSpan={3}>Total a aplicar ({pares.length} pares)</td><td className="px-2 py-1.5 text-right tabular-nums">{fmtCLP(total)}</td><td /></tr>
                <tr className={`border-t text-[11px] ${sumAnts === sumDocs ? 'text-emerald-700' : 'text-red-700'}`} data-testid="preview-cuadratura">
                  <td className="px-2 py-1" colSpan={3}>Σ anticipos ({info.anticipo}) {fmtCLP(sumAnts)} · Σ documentos ({info.documento}) {fmtCLP(sumDocs)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{sumAnts === sumDocs ? 'Cuadra' : `Diferencia: ${fmtCLP(sumAnts - sumDocs)}`}</td><td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {!resultado && errores.length > 0 && (
          <ul className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 space-y-0.5" data-testid="preview-errores">{errores.map((e) => <li key={e}>• {e}</li>)}</ul>
        )}
        {!resultado && advertencias.length > 0 && (
          <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 space-y-0.5" data-testid="preview-advertencias">{advertencias.map((w) => <li key={w}><AlertTriangle className="w-3 h-3 inline mr-1" />{w}</li>)}</ul>
        )}

        {resultado && (
          <div className="border rounded max-h-[50vh] overflow-y-auto">
            <table className="w-full text-xs" data-testid="tabla-resultado">
              <thead className="bg-muted sticky top-0"><tr><th className="text-left px-2 py-1.5">{info.anticipo}</th><th className="text-left px-2 py-1.5">{info.documento}</th><th className="text-right px-2 py-1.5">Monto</th><th className="text-left px-2 py-1.5">Resultado</th></tr></thead>
              <tbody>
                {resultado.resultados.map((r, i) => (
                  <tr key={i} className="border-t" data-testid={`res-${i}-${r.ok ? 'ok' : 'error'}`}>
                    <td className="px-2 py-1 font-mono">N°{r.anticipo_ref || '—'}</td>
                    <td className="px-2 py-1 font-mono">{r.documento_numero}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtCLP(r.monto)}</td>
                    <td className="px-2 py-1">
                      {r.ok ? (
                        <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />
                          {info.documento} {r.documento_estado === 'pagado' ? `${info.accionDoc === 'pago' ? 'pagada' : 'cobrada'} completa` : `${info.accionDoc} parcial (saldo ${fmtCLP(r.documento_saldo)})`} · anticipo {r.anticipo_estado === 'aplicado' ? 'consumido completo' : `parcial (disp. ${fmtCLP(r.anticipo_disponible)})`}
                        </span>
                      ) : <span className="text-red-700 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> {r.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          {resultado ? <Button onClick={cerrar} data-testid="btn-cerrar-resultado">Cerrar</Button> : (
            <>
              <Button variant="outline" onClick={onClose} disabled={saving} data-testid="btn-cancelar-aplicar">Cancelar</Button>
              <Button onClick={aplicar} disabled={saving || !pares.length || errores.length > 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-confirmar-aplicar">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Aplicar {pares.length} par(es) · {fmtCLP(total)}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
